// QC specification attached to an Analysis definition (F-B1): the measured parameters with
// their acceptance criteria, plus a calculation formula. This is the GMP "method spec" the
// result-entry flow (F-C2) reads — judging is done live in the FE and authoritatively by the
// server's recompute against the SAME server-side spec.
//
// SPEC-021 (B2): there is NO /lims/specs endpoint. The spec is FOLDED onto the analysis-
// definition payload — it arrives as the nested `spec` key on every definition dump and is
// WRITTEN via the definition save (POST /analysis-definitions, PATCH /analysis-definition/<id>)
// together with an optional `specChangeReason` the server audits when the spec actually changed.
// CRITICAL: the server snapshots + recomputes results from ITS spec rows, so the spec must be
// written through the definition path — a spec that only lived FE-side would make every result
// recompute against an empty snapshot.
//
// This slice therefore has no fetch/upsert of its own (B-D3: the selector API is kept; the data
// source changed): the reducer LISTENS to the analysisDefinitions actions and mirrors each
// definition's nested `spec` into the same { [analysisDefinitionId]: { parameters, formula } }
// map every consumer already reads.

import { CALC_INPUT_SOURCES } from "./calcEngine"

// state shape: { [analysisDefinitionId]: { parameters: [...], formula: "<string>" } }
//
// A parameter is { id, name, unit, significantDigits, criterionType, min, max } and — since
// SPEC-016 (PRD-005 Area 2) — also { kind, inputs, formula, aggregate, templateId,
// individualTolerancePercent } for a CALCULATED parameter (a MEASURED parameter keeps these at
// their inert defaults). The spec-level `formula` is retained as a legacy reference.
const initialState = {}

// A spec bound (min/max) is the raw value the analyst typed — a string or number, or null when
// unset. Coerce anything non-primitive (e.g. an object persisted by an earlier build) to null so
// it can never reach the result-judging logic or the editor's text inputs as "[object Object]".
function normalizeBound(v) {
  if (v === null || v === undefined || v === "") return null
  if (typeof v === "number" || typeof v === "string") return v
  return null
}

// SPEC-016 (PRD-005 Area 2) — a parameter is either MEASURED (direct entry, the legacy default)
// or CALCULATED (the analyst enters named raw inputs and the calculation engine derives + judges
// the value). `kind` absent ⇒ "measured" so every pre-SPEC-016 spec reads unchanged.
export const SPEC_PARAMETER_KIND = { MEASURED: "measured", CALCULATED: "calculated" }
// The allowed input sources now live with the engine (SPEC-023 added "parameter"); re-exported
// here so existing consumers of this module keep working unchanged.
export { CALC_INPUT_SOURCES }

// Normalise one declared input of a calculated parameter: { key, label, unit, source } — plus
// `parameterId` when the source is "parameter" (SPEC-023: the input binds to another parameter
// of the same spec by its STABLE id; names may change). A blank key / unknown source falls back
// to safe defaults so the engine + UI never see garbage; a missing/blank parameterId is kept as
// null (NOT silently downgraded to manual) so the reference validation flags it before save.
function normalizeInput(input) {
  const src = CALC_INPUT_SOURCES.includes(input && input.source) ? input.source : "manual"
  const normalized = {
    key: typeof (input && input.key) === "string" ? input.key.trim() : "",
    label: typeof (input && input.label) === "string" ? input.label : "",
    unit: typeof (input && input.unit) === "string" ? input.unit : "",
    source: src,
  }
  if (src === "parameter") {
    normalized.parameterId =
      typeof (input && input.parameterId) === "string" && input.parameterId.trim() !== ""
        ? input.parameterId
        : null
  }
  return normalized
}

// Normalise the new calculated-parameter fields (kind / inputs / per-parameter formula /
// aggregate / templateId / individualTolerancePercent), preserving the measured-parameter shape
// untouched. For a measured parameter the calculated-only fields are forced to their inert
// defaults, so toggling kind back and forth never leaves stale data on the record.
function normalizeParameter(p) {
  const param = p && typeof p === "object" ? p : {}
  const base = { ...param, min: normalizeBound(param.min), max: normalizeBound(param.max) }
  const kind = param.kind === SPEC_PARAMETER_KIND.CALCULATED ? SPEC_PARAMETER_KIND.CALCULATED : SPEC_PARAMETER_KIND.MEASURED
  if (kind !== SPEC_PARAMETER_KIND.CALCULATED) {
    // A MEASURED parameter has NO calculated state at all — including no per-parameter formula.
    // Keeping a stale formula on a measured parameter is the latent data-integrity hole behind
    // the "template seeded a formula but the inputs were wiped" bug (PRD-006 K21): this branch
    // wiped `inputs` to [] but PRESERVED `formula`, so a parameter that lost its calculated `kind`
    // ended up with a formula referencing inputs that no longer existed → "Unknown name(s): …".
    // Clearing the formula too keeps the wipe symmetric: a measured parameter has neither inputs
    // nor a formula, so the two can never diverge.
    return {
      ...base,
      kind: SPEC_PARAMETER_KIND.MEASURED,
      inputs: [],
      formula: "",
      aggregate: null,
      templateId: null,
      individualTolerancePercent: null,
    }
  }
  const inputs = (Array.isArray(param.inputs) ? param.inputs : []).map(normalizeInput).filter((i) => i.key !== "")
  const aggregate =
    param.aggregate && typeof param.aggregate.inputKey === "string" && param.aggregate.inputKey.trim() !== ""
      ? { inputKey: param.aggregate.inputKey.trim() }
      : null
  const indiv =
    param.individualTolerancePercent === null ||
    param.individualTolerancePercent === undefined ||
    param.individualTolerancePercent === ""
      ? null
      : param.individualTolerancePercent
  return {
    ...base,
    kind: SPEC_PARAMETER_KIND.CALCULATED,
    inputs,
    formula: typeof param.formula === "string" ? param.formula : "",
    aggregate,
    templateId: typeof param.templateId === "string" && param.templateId !== "" ? param.templateId : null,
    individualTolerancePercent: indiv,
  }
}

// Normalise a full parameter list. Exported so the definition editor can build the exact
// persisted shape it sends on the definition payload (the server stores it verbatim as JSONB).
export function normalizeParameters(parameters) {
  return (Array.isArray(parameters) ? parameters : []).map(normalizeParameter)
}

// F8 (SPEC-040) — the allowed consumable-requirement types (mirrors CONSUMABLE_TYPES in
// limsConsumables.js; kept here to avoid a cross-slice import for a tiny constant).
export const REQUIRED_CONSUMABLE_TYPES = ["reagent", "standard", "column", "glassware"]

// A requirement-field value counts as "blank" (unset) when null/undefined/empty-string, or a
// whitespace-only string. Quantity is advisory, so a blank field is "unspecified", not 0.
function isBlankReqValue(v) {
  if (v === null || v === undefined) return true
  return typeof v === "string" && v.trim() === ""
}

// Normalise the optional quantity object of a requirement → { min, max, unit } or null when every
// field is blank (quantity is advisory; an all-blank quantity is "unspecified").
function normalizeQuantity(quantity) {
  const q = quantity && typeof quantity === "object" ? quantity : null
  if (!q) return null
  if (isBlankReqValue(q.min) && isBlankReqValue(q.max) && isBlankReqValue(q.unit)) return null
  return {
    min: q.min === undefined ? null : q.min,
    max: q.max === undefined ? null : q.max,
    unit: typeof q.unit === "string" ? q.unit : "",
  }
}

// Normalise one consumable requirement on the analysis template (F8): a high-level demand the
// run's consumable selection must satisfy — NOT a serial/lot number. Shape:
//   { type, name, quantity: { min, max, unit } | null, standardClass | null, notes }
// An unknown/blank type folds to "reagent" (mirrors consumableType); standardClass is kept as-is
// or null. Garbage in never reaches the picker filter or the completeness gate.
function normalizeRequirement(req) {
  const r = req && typeof req === "object" ? req : {}
  return {
    type: REQUIRED_CONSUMABLE_TYPES.includes(r.type) ? r.type : "reagent",
    name: typeof r.name === "string" ? r.name : "",
    quantity: normalizeQuantity(r.quantity),
    standardClass: typeof r.standardClass === "string" && r.standardClass !== "" ? r.standardClass : null,
    notes: typeof r.notes === "string" ? r.notes : "",
  }
}

// Normalise a full consumable-requirements list. Exported so the definition editor builds the
// exact persisted shape it folds onto the definition payload (`spec.requiredConsumables`).
export function normalizeRequiredConsumables(requirements) {
  return (Array.isArray(requirements) ? requirements : []).map(normalizeRequirement)
}

// Whether a definition dump carries a usable nested spec object (null/absent = no spec row).
function specOf(definition) {
  return definition && definition.spec && typeof definition.spec === "object" ? definition.spec : null
}

// Mirror one definition's nested spec into the map (or drop the entry when it has none).
function applyDefinition(map, definition) {
  if (!definition || !definition.id) return map
  const spec = specOf(definition)
  const next = { ...map }
  if (spec) {
    next[definition.id] = {
      parameters: normalizeParameters(spec.parameters),
      formula: spec.formula || "",
      requiredConsumables: normalizeRequiredConsumables(spec.requiredConsumables),
    }
  } else if (next[definition.id]) {
    delete next[definition.id]
  }
  return next
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "analysisDefinitions/fetch": {
      const map = {}
      ;(action.res || []).forEach((item) => {
        const spec = specOf(item)
        if (spec && item.id) {
          map[item.id] = {
            parameters: normalizeParameters(spec.parameters),
            formula: spec.formula || "",
            requiredConsumables: normalizeRequiredConsumables(spec.requiredConsumables),
          }
        }
      })
      return map
    }
    case "analysisDefinitions/create":
    case "analysisDefinitions/edit":
      return applyDefinition(state, action.res)
    case "analysisDefinitions/remove": {
      if (!action.res || !state[action.res.id]) return state
      const next = { ...state }
      delete next[action.res.id]
      return next
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// The spec for one analysis definition (or undefined if none stored yet).
export const selectSpec = (state, analysisDefinitionId) => state.limsSpecs[analysisDefinitionId]

// Whether a spec parameter is calculated (engine-derived) vs measured (direct entry). `kind`
// absent reads as measured, so legacy parameters are correctly treated as measured.
export const isCalculatedParameter = (parameter) =>
  !!parameter && parameter.kind === SPEC_PARAMETER_KIND.CALCULATED
